import { useEffect, useState } from 'react';
import { notification } from 'antd';
import { t } from '../i18n';

interface SaveResult {
  status?: 'error' | 'finished';
  segmentId?: number;
  triggerId?: number;
}

class TargetClass {
  private _data: SaveResult | null = null;

  constructor() {
    this._data = null;
  }

  public setData(data: any) {
    this._data = data;
  }

  public getData() {
    return this._data;
  }
}

const useUserSegmentFrame = (
  configs: {
    eventResize: string;
    eventSubmit: string;
    eventSubmitDone: string;
  },
  onLoad?: (state: boolean) => void
) => {
  const { eventResize, eventSubmit, eventSubmitDone } = configs;
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [iframe, setIframe] = useState<any>(null);
  const [size, setSize] = useState<{ height?: number; width?: number }>({
    height: 0,
  });
  const [targetClass, setTargetClass] = useState<TargetClass>(
    new TargetClass()
  );

  const getContentWindow = () => iframe && iframe.contentWindow;

  const submit = async () => {
    setLoading(true);
    let data: SaveResult | undefined;

    try {
      const contentWindow = getContentWindow();

      if (contentWindow) {
        const maxTimes = 30;
        const onLoop = (
          callback: (data: SaveResult | undefined) => void,
          count = 0
        ) => {
          const data = targetClass.getData();
          setTimeout(() => {
            if (count > maxTimes) {
              callback({ status: undefined });
            } else if (data) {
              callback(data);
            } else {
              onLoop(callback, count + 1);
            }
          }, 500);
        };

        const getMessageResponse = (): Promise<SaveResult | undefined> => {
          return new Promise(resolve => {
            onLoop(data => {
              resolve(data);
            });
          });
        };

        contentWindow.postMessage(
          {
            type: eventSubmit,
            content: '',
          },
          '*'
        );

        targetClass.setData(null);

        data = await getMessageResponse();
      }

      if (!contentWindow) {
        notification.error({
          message: t('UserSegment.WaitIframeLoaded'),
        });
      } else if (!data?.status) {
        notification.error({
          message: t('UserSegment.IFrameNotResponding'),
        });
      }
    } catch (e) {}

    setLoading(false);

    return {
      segmentId: data?.segmentId,
      triggerId: data?.triggerId,
      state: data?.status === 'finished',
    };
  };
  const resetSize = () => {
    setIsLoaded(false);
    setSize({ height: 0 });
  };

  const eventListener = (event: MessageEvent, _iframe: any) => {
    const { data: _data, origin } = event;
    if (origin !== window.REACT_USER_SEGMENT_UI) {
      return;
    }

    if (!iframe) {
      setIframe(_iframe);
    }
    const { type, data } = _data;
    if (type === eventSubmitDone) {
      const { status } = (data || {}) as SaveResult;
      if (status === 'error' || status === 'finished') {
        targetClass.setData(data);
      }
    }
    if (type === eventResize) {
      setSize(data);
    }
  };

  useEffect(() => {
    setIsLoaded(false);
    onLoad && onLoad(false);
    if (!targetClass) {
      setTargetClass(new TargetClass());
    }
    targetClass.setData(null);
  }, []);

  useEffect(() => {
    if (!isLoaded && size.height) {
      setIsLoaded(true);
      onLoad && onLoad(true);
    }
  }, [size.height, isLoaded]);

  return {
    size,
    loading,
    iframe,
    submit,
    resetSize,
    setIframe,
    eventListener,
  };
};

export default { useUserSegmentFrame };
